import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Eye, EyeOff } from 'lucide-react'
import api from '../../services/api'
import { formatPhone } from '../../utils/formatters'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import toast from 'react-hot-toast'

// Campos comuns a criação e edição
const baseDriverFields = {
  name: z.string().min(1, 'Nome é obrigatório').min(2, 'Nome muito curto').max(100, 'Nome muito longo'),
  phone: z.string().min(1, 'Telefone é obrigatório').transform(val => val.replace(/\D/g, '')).pipe(z.string().regex(/^\d{10,11}$/, 'Telefone inválido (10 ou 11 dígitos com DDD)')),
  email: z.string().min(1, 'Email é obrigatório').email('Email inválido'),
}

// Senha só existe (e só é obrigatória) na criação - no edit o campo nem
// aparece no form, então nem faz parte do schema de edição.
const createDriverSchema = z.object({
  ...baseDriverFields,
  password: z.string().min(1, 'Senha é obrigatória').min(6, 'Senha deve ter no mínimo 6 caracteres'),
})

const editDriverSchema = z.object(baseDriverFields)

export default function DriverForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEditing = !!id

  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(isEditing ? editDriverSchema : createDriverSchema),
  })

  // Busca dados se estiver editando
  const { data: driver, isLoading } = useQuery({
    queryKey: ['driver', id],
    queryFn: async () => {
      const response = await api.get(`/api/drivers/${id}`)
      return response.data.data.driver
    },
    enabled: isEditing,
  })

  // Preenche form com dados existentes
  useEffect(() => {
    if (driver) {
      reset({
        name: driver.name,
        phone: formatPhone(driver.phone),
        email: driver.email,
      })
    }
  }, [driver, reset])

  // Mutation para criar/atualizar
  const mutation = useMutation({
    mutationFn: async (data) => {
      if (isEditing) {
        return api.put(`/api/drivers/${id}`, data)
      }
      return api.post('/api/drivers', data)
    },
    onSuccess: () => {
      toast.success(isEditing ? 'Motorista atualizado!' : 'Motorista cadastrado!')
      queryClient.invalidateQueries({ queryKey: ['drivers'] })
      navigate('/drivers')
    },
    onError: (error) => {
      if (error.response?.data?.error === 'ALREADY_EXISTS') {
        toast.error('Já existe um motorista cadastrado com este email')
        return
      }
      toast.error(error.response?.data?.message || 'Erro ao salvar motorista')
    },
  })

  const onSubmit = (data) => {
    // PUT nunca leva password no body - no edit o campo nem existe no form
    const payload = isEditing
      ? { name: data.name, phone: data.phone, email: data.email }
      : { name: data.name, phone: data.phone, email: data.email, password: data.password }

    mutation.mutate(payload)
  }

  if (isEditing && isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/drivers')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="page-title">
            {isEditing ? 'Editar Motorista' : 'Novo Motorista'}
          </h1>
          <p className="page-subtitle">
            {isEditing ? 'Atualize os dados do motorista.' : 'Cadastre um novo motorista da sua equipe.'}
          </p>
        </div>
      </div>

      {/* Formulário */}
      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome completo"
              placeholder="Nome do motorista"
              error={errors.name?.message}
              {...register('name')}
            />
            <Input
              label="Telefone"
              placeholder="(92) 99999-8888"
              error={errors.phone?.message}
              {...register('phone')}
            />
            <Input
              label="Email"
              type="email"
              placeholder="email@exemplo.com"
              error={errors.email?.message}
              {...register('email')}
            />

            {!isEditing && (
              <div className="relative">
                <Input
                  label="Senha"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Mínimo 6 caracteres"
                  error={errors.password?.message}
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            )}
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/drivers')}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              isLoading={isSubmitting || mutation.isPending}
            >
              {isEditing ? 'Salvar alterações' : 'Cadastrar'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
